import React, { useState, useRef, useEffect } from "react";
import { Clock, Play, ArrowRight, RotateCcw } from "lucide-react";
import { IconArrowLeft, IconEdit, IconThreeDots, IconAccept, IconClose, IconFinish, IconDuplicate } from "../../icons";
import { PrimaryButton, ActionButton } from "../../ListaDemandas/CabecalhoListaDemanda/styles";
import { Container, GrupoEsquerdo, GrupoDireito, DropDownMenu } from "./styles";
import { useHistory } from "react-router-dom";
import { IProposta } from "../../../utility/IProposta";
import moment from "moment";
import "moment/locale/pt-br";
import Moment from "react-moment";
import { comum } from "../../../styles/Themes/light";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useAuth } from "../../../contexts/UserContext";
import { podeEditarDemanda } from "../../../helpers/permissoesUsuario";

interface CabecalhoDemandaProps {
    demanda?: IProposta;
    loading?: boolean;
    acessoNegado?: boolean;
    onEditClick?: () => void;
    isEditing?: boolean;
    onCancelarEdicao?: () => void;
    onSalvarEdicao?: () => void;
    hasChanges?: boolean;
    isValidating?: boolean;
    hasErrors?: boolean;
    onStartTriagemClick?: () => void;
    onEncaminharContratacaoClick?: () => void;
    onAceitarDemandaClick?: () => void;
    onRecusarDemandaClick?: () => void;
    onIniciarExecucaoClick?: () => void;
    onEntregarClick?: () => void;
    onFinalizarClick?: () => void;
    onReabrirExecucaoClick?: () => void;
    onCancelarClick?: () => void;
    onDuplicarClick?: () => void;
}

export default function CabecalhoDemanda({ demanda, loading, acessoNegado = false, onEditClick, isEditing, onCancelarEdicao, onSalvarEdicao, hasChanges, isValidating = false, hasErrors = false, onStartTriagemClick, onEncaminharContratacaoClick, onAceitarDemandaClick, onRecusarDemandaClick, onIniciarExecucaoClick, onEntregarClick, onFinalizarClick, onReabrirExecucaoClick, onCancelarClick, onDuplicarClick }: CabecalhoDemandaProps) {
    const history = useHistory();
    const { user } = useAuth();
    const podeEditar = podeEditarDemanda(user);
    const [openMenu, setOpenMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenu(false);
            }
        };

        if (openMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenu]);

    const formatarPrazoAceite = (prazoAceite: string | null | undefined): React.ReactNode => {
        if (!prazoAceite) {
            return "Prazo de aceite não definido";
        }
        
        const prazo = moment(prazoAceite);
        const agora = moment();
        
        if (prazo.isBefore(agora)) {
            return "Prazo de aceite expirado";
        }
        
        return (
            <>
                <Moment locale='pt-br' fromNow ago>{prazoAceite}</Moment> restantes para o aceite
            </>
        );
    };

    const isAdminOuOperador = user?.profile === 'admin' || user?.profile === 'operador';
    
    const mostrarBotaoIniciarTriagem = !acessoNegado &&
        !isEditing && 
        demanda?.status === 'Pendente' && 
        isAdminOuOperador &&
        onStartTriagemClick;

    const mostrarBotaoEncaminharContratacao = !acessoNegado &&
        !isEditing && 
        demanda?.status === 'Em Triagem' && 
        isAdminOuOperador &&
        onEncaminharContratacaoClick;

    const prazoAceiteExpirado = demanda?.prazo_aceite ? new Date(demanda.prazo_aceite) < new Date() : false;

    const mostrarBotoesAceitarRecusar = !acessoNegado &&
        !isEditing && 
        demanda?.status === 'Em Contratação' && 
        (isAdminOuOperador ||
            (user?.profile === 'comum' && demanda?.id_usuario === Number(user?.id))) &&
        !prazoAceiteExpirado &&
        onAceitarDemandaClick &&
        onRecusarDemandaClick;
    
    const mostrarBotaoIniciar = !acessoNegado &&
        !isEditing && 
        demanda?.status === 'Aguardando Execução' && 
        demanda?.id_usuario && 
        (isAdminOuOperador ||
            (user?.profile === 'comum' && demanda?.id_usuario === Number(user?.id))) &&
        onIniciarExecucaoClick;

    const mostrarBotaoEntregar = !acessoNegado &&
        !isEditing && 
        demanda?.status === 'Em Execução' && 
        (isAdminOuOperador ||
            (user?.profile === 'comum' && demanda?.id_usuario === Number(user?.id))) &&
        onEntregarClick;

    const mostrarBotoesEmConferencia = !acessoNegado &&
        !isEditing && 
        demanda?.status === 'Em Conferência' && 
        isAdminOuOperador &&
        onFinalizarClick &&
        onReabrirExecucaoClick;

    const statusPermitidosCancelamento = ['Pendente', 'Em Triagem', 'Em Contratação', 'Aguardando Execução', 'Em Execução', 'Em Conferência'];
    const mostrarBotaoCancelar = !acessoNegado &&
        !isEditing && 
        demanda?.status && 
        statusPermitidosCancelamento.includes(demanda.status) &&
        isAdminOuOperador &&
        onCancelarClick;

    const mostrarBotaoDuplicar = !acessoNegado &&
        !isEditing && 
        isAdminOuOperador &&
        onDuplicarClick;

    const statusAtual = demanda?.status;
    const statusComDropdown: Record<string, { incluirCancelar: boolean }> = {
        'Pendente': { incluirCancelar: true },
        'Em Triagem': { incluirCancelar: true },
        'Em Contratação': { incluirCancelar: true },
        'Aguardando Execução': { incluirCancelar: true },
        'Em Execução': { incluirCancelar: true },
        'Em Conferência': { incluirCancelar: true },
        'Finalizada': { incluirCancelar: false },
        'Cancelada': { incluirCancelar: false }
    };

    const statusTemDropdown = !acessoNegado &&
        statusAtual && 
        statusComDropdown[statusAtual] && 
        isAdminOuOperador;
    
    const botoesNoDropdown: Array<{ onClick?: () => void; icon: React.ReactNode; label: string; title: string }> = [];
    
    if (statusTemDropdown) {
        const configStatus = statusComDropdown[statusAtual!];
        
        if (!isEditing && onEditClick && podeEditar) {
            botoesNoDropdown.push({ 
                onClick: onEditClick, 
                icon: <IconEdit />, 
                label: 'Editar', 
                title: 'Editar demanda' 
            });
        }
        
        if (mostrarBotaoDuplicar) {
            botoesNoDropdown.push({ 
                onClick: onDuplicarClick, 
                icon: <IconDuplicate />, 
                label: 'Duplicar', 
                title: 'Duplicar demanda' 
            });
        }
        
        if (configStatus.incluirCancelar && mostrarBotaoCancelar) {
            botoesNoDropdown.push({ 
                onClick: onCancelarClick, 
                icon: <IconClose color={comum.colors.primaryRed400} />, 
                label: 'Cancelar', 
                title: 'Cancelar demanda' 
            });
        }
    }

    const statusComBotaoDireto = ['Pendente', 'Em Triagem', 'Em Contratação', 'Aguardando Execução', 'Em Execução', 'Em Conferência', 'Finalizada', 'Cancelada'];
    const deveEsconderBotoesDiretos = statusAtual && statusComBotaoDireto.includes(statusAtual) && isAdminOuOperador;
    
    return (

        <>
        {loading ? (
            <Container>
                <GrupoEsquerdo>
                    <Skeleton height={32} width={80} style={{marginRight: '20px'}} />
                    <Skeleton height={16} width={1} style={{margin: '0 20px'}} />
                    <Skeleton height={32} width={200} style={{marginRight: '40px'}} />
                    <Skeleton height={20} width={200} />
                </GrupoEsquerdo>
                <GrupoDireito>
                    <Skeleton height={40} width={120} style={{marginRight: '16px'}} />
                    <Skeleton height={40} width={40} />
                </GrupoDireito>
            </Container>
        ) : (

        <Container>
            <GrupoEsquerdo>
                <div className="voltar-container" onClick={() => { history.goBack(); }}>
                <IconArrowLeft />
                    <div className="voltar">Voltar</div>
                </div>
                <div className="divider" />
                <h1>Demanda #{demanda?.id_proposta}</h1>
                <div className="timer">
                    {
                        (['Pendente', 'Interrompida', 'Em Contratação', 'Aguardando Execução'].includes(demanda?.status || '') ||
                         (demanda?.status && ['Em Execução', 'Em Conferência'].includes(demanda.status))) &&
                        <Clock color={comum.colors.primaryRed400} />
                    }
                    {
                        demanda?.status === 'Pendente' &&
                        <div className="moment-container">
                            {formatarPrazoAceite(demanda.prazo_aceite)}
                        </div>
                    }
                    {
                        demanda?.status === 'Interrompida' &&
                        <div className="moment-container">
                            {formatarPrazoAceite(demanda.prazo_aceite)}
                        </div>
                    }
                    {
                        demanda?.status === 'Em Contratação' &&
                        <div className="moment-container">
                            {formatarPrazoAceite(demanda.prazo_aceite)}
                        </div>
                    }
                    {
                        (demanda?.status === 'Aguardando Execução' || 
                         (demanda?.status && ['Em Execução', 'Em Conferência'].includes(demanda.status))) &&
                        demanda?.prazo_para_conclusao &&
                        <div className="moment-container">
                        <Moment locale='pt-br' fromNow ago>{demanda.prazo_para_conclusao}</Moment> restantes para a conclusão
                        </div>
                    }
                </div>
            </GrupoEsquerdo>
            <GrupoDireito>
                {mostrarBotaoIniciarTriagem && (
                    <PrimaryButton
                        title="Iniciar triagem"
                        onClick={onStartTriagemClick}
                    >
                        <Play />
                        Iniciar triagem
                    </PrimaryButton>
                )}
                {mostrarBotaoEncaminharContratacao && (
                    <PrimaryButton
                        title="Encaminhar para contratação"
                        onClick={onEncaminharContratacaoClick}
                    >
                        <ArrowRight />
                        Encaminhar para contratação
                    </PrimaryButton>
                )}
                {mostrarBotoesAceitarRecusar && (
                    <>
                        <PrimaryButton
                            title="Aceitar demanda"
                            onClick={onAceitarDemandaClick}
                        >
                            <IconAccept />
                            Aceitar
                        </PrimaryButton>
                        <ActionButton
                            title="Recusar demanda"
                            onClick={onRecusarDemandaClick}
                        >
                            <IconClose color={comum.colors.primaryRed400} />
                            Recusar
                        </ActionButton>
                    </>
                )}
                {mostrarBotaoIniciar && (
                    <PrimaryButton
                        title="Iniciar"
                        onClick={onIniciarExecucaoClick}
                    >
                        <Play />
                        Iniciar
                    </PrimaryButton>
                )}
                {mostrarBotaoEntregar && (
                    <PrimaryButton
                        title="Entregar"
                        onClick={onEntregarClick}
                    >
                        <IconFinish />
                        Entregar
                    </PrimaryButton>
                )}
                {mostrarBotoesEmConferencia && (
                    <>
                        <PrimaryButton
                            title="Finalizar demanda"
                            onClick={onFinalizarClick}
                        >
                            <IconFinish />
                            Finalizar
                        </PrimaryButton>
                        <PrimaryButton
                            title="Reabrir execução"
                            onClick={onReabrirExecucaoClick}
                        >
                            <RotateCcw />
                            Reabrir execução
                        </PrimaryButton>
                    </>
                )}
                {!acessoNegado && !deveEsconderBotoesDiretos && mostrarBotaoCancelar && (
                    <ActionButton
                        title="Cancelar demanda"
                        onClick={onCancelarClick}
                    >
                        <IconClose color={comum.colors.primaryRed400} />
                        Cancelar
                    </ActionButton>
                )}
                {!acessoNegado && !deveEsconderBotoesDiretos && mostrarBotaoDuplicar && (
                    <PrimaryButton
                        title="Duplicar demanda"
                        onClick={onDuplicarClick}
                    >
                        <IconDuplicate />
                        Duplicar
                    </PrimaryButton>
                )}
                {!acessoNegado && !deveEsconderBotoesDiretos && !isEditing && onEditClick && podeEditar && (
                    <PrimaryButton
                        title="Editar demanda"
                        onClick={onEditClick}
                    >
                        <IconEdit />
                        Editar
                    </PrimaryButton>
                )}
                {!acessoNegado && isEditing && onCancelarEdicao && onSalvarEdicao && podeEditar && (
                    <>
                        <ActionButton
                            title="Cancelar alterações"
                            onClick={onCancelarEdicao}
                            disabled={isValidating}
                        >
                            Cancelar alterações
                        </ActionButton>
                        <PrimaryButton
                            title={hasErrors ? 'Corrija os erros antes de salvar' : 'Salvar alterações'}
                            onClick={onSalvarEdicao}
                            className={(!hasChanges || isValidating) ? 'disabled' : ''}
                            disabled={!hasChanges || isValidating}
                        >
                            {isValidating ? 'Salvando...' : 'Salvar alterações'}
                        </PrimaryButton>
                    </>
                )}
                {botoesNoDropdown.length > 0 && (
                    <DropDownMenu 
                        onClick={() => setOpenMenu(!openMenu)}
                        ref={menuRef}
                    >
                        <div>
                            <IconThreeDots />
                        </div>
                        <div className={openMenu ? 'hidden-menu' : 'hidden-menu no-show'}>
                            {botoesNoDropdown.map((botao, index) => (
                                <div
                                    key={index}
                                    className="button"
                                    onClick={() => {
                                        botao.onClick?.();
                                        setOpenMenu(false);
                                    }}
                                    title={botao.title}
                                >
                                    {botao.icon}
                                    {botao.label}
                                </div>
                            ))}
                        </div>
                    </DropDownMenu>
                )}
            </GrupoDireito>
        </Container>
        )}
        </>
    )
}